import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import ToastContext from "../contexts/ToastContext";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import JustificationModal from "../components/JustificationModal";
import { FaArrowLeft } from "react-icons/fa";

const getApiErrorMessage = (error, fallbackMessage) => {
  const status = error?.response?.status;
  const data = error?.response?.data;

  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed.startsWith("<!DOCTYPE") || trimmed.startsWith("<html")) {
      return fallbackMessage;
    }
    return trimmed || fallbackMessage;
  }

  const serverMessage = data?.message;
  const serverCode = data?.code;
  if (serverMessage && serverCode) {
    return `${serverMessage} (${serverCode})`;
  }
  if (serverMessage) {
    return serverMessage;
  }
  if (status) {
    return `${fallbackMessage} (HTTP ${status})`;
  }
  return fallbackMessage;
};

function DetalhesCandidato() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useContext(ToastContext);
  const [candidato, setCandidato] = useState(null);
  const [inscricoes, setInscricoes] = useState([]);
  const [loadingInscricoes, setLoadingInscricoes] = useState(true);
  const [invalidacaoModalOpen, setInvalidacaoModalOpen] = useState(false);
  const [invalidacaoTarget, setInvalidacaoTarget] = useState(null);
  const [isRevalidarFlow, setIsRevalidarFlow] = useState(false);

  useEffect(() => {
    const fetchCandidato = async () => {
      try {
        const { data } = await api.get(`/users/${id}`);
        setCandidato(data);
      } catch (error) {
        addToast("Erro ao buscar dados do candidato.", "error");
        navigate(-1); // Volta para a página anterior em caso de erro
      }
    };
    fetchCandidato();
  }, [id, addToast, navigate]);

  useEffect(() => {
    const fetchInscricoes = async () => {
      try {
        setLoadingInscricoes(true);
        const { data } = await api.get(`/inscricoes/usuario/${id}`);
        setInscricoes(Array.isArray(data) ? data : []);
      } catch (error) {
        addToast("Erro ao buscar inscrições do candidato.", "error");
      } finally {
        setLoadingInscricoes(false);
      }
    };
    fetchInscricoes();
  }, [id, addToast]);

  const openInvalidar = (inscricao) => {
    setInvalidacaoTarget(inscricao);
    setIsRevalidarFlow(false);
    setInvalidacaoModalOpen(true);
  };

  const openRevalidar = (inscricao) => {
    setInvalidacaoTarget(inscricao);
    setIsRevalidarFlow(true);
    setInvalidacaoModalOpen(true);
  };

  const closeInvalidacaoModal = () => {
    setInvalidacaoModalOpen(false);
    setInvalidacaoTarget(null);
    setIsRevalidarFlow(false);
  };

  const submitInvalidacao = async (justificativaText) => {
    if (!invalidacaoTarget) return;
    try {
      await api.put(`/inscricoes/${invalidacaoTarget.id}/invalidacao`, {
        invalidada: !isRevalidarFlow,
        justificativa: isRevalidarFlow ? null : justificativaText,
      });
      addToast(
        isRevalidarFlow ? "Inscrição revalidada." : "Inscrição invalidada.",
        "success"
      );
      closeInvalidacaoModal();
      const { data } = await api.get(`/inscricoes/usuario/${id}`);
      setInscricoes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao atualizar invalidação da inscrição", {
        status: error?.response?.status,
        data: error?.response?.data,
        dataString:
          typeof error?.response?.data === "string"
            ? error.response.data
            : JSON.stringify(error?.response?.data ?? null),
        headers: error?.response?.headers,
        message: error?.message,
      });
      addToast(
        getApiErrorMessage(error, "Erro ao atualizar invalidação da inscrição."),
        "error"
      );
    }
  };

  if (!candidato) {
    return <p>Carregando...</p>;
  }

  return (
    <>
      <PageHeader title="Detalhes do Candidato">
        <button onClick={() => navigate(-1)} className="button-secondary">
          <FaArrowLeft />
          <span>Voltar</span>
        </button>
      </PageHeader>
      <Card>
        <div className="detail-grid">
          <div className="detail-grid__header">
            <h2 className="detail-grid__title">{`${candidato.nome} ${candidato.sobrenome}`}</h2>
            <span className="detail-grid__subtitle">{candidato.email}</span>
          </div>
          <div className="detail-field">
            <strong>CPF</strong>
            <p>{candidato.cpf}</p>
          </div>
          <div className="detail-field">
            <strong>Telefone</strong>
            <p>{candidato.telefone || "Não informado"}</p>
          </div>
          <div className="detail-field">
            <strong>RG</strong>
            <p>{candidato.rg || "Não informado"}</p>
          </div>
          <div className="detail-field">
            <strong>Data de Nascimento</strong>
            <p>
              {candidato.data_nascimento
                ? new Date(candidato.data_nascimento).toLocaleDateString()
                : "Não informada"}
            </p>
          </div>
          <div className="detail-field">
            <strong>Portador de Deficiência</strong>
            <p>
              {candidato.possui_deficiencia === null ||
              candidato.possui_deficiencia === undefined
                ? "Não informado"
                : candidato.possui_deficiencia
                ? "Sim"
                : "Não"}
            </p>
          </div>
          <div className="detail-field">
            <strong>Raça</strong>
            <p>
              {(() => {
                if (!candidato.raca || candidato.raca === "NAO_INFORMADO") {
                  return "Não informada";
                }
                if (candidato.raca === "Nao declarada") {
                  return "Prefiro não declarar";
                }
                return candidato.raca;
              })()}
            </p>
          </div>
          <div className="detail-field">
            <strong>Sexo</strong>
            <p>{candidato.sexo || "Não informado"}</p>
          </div>
          <div className="detail-field detail-grid__header">
            <strong>Endereço</strong>
            <p>
              {candidato.endereco
                ? `${candidato.endereco}, ${candidato.numero} - ${candidato.bairro}, ${candidato.municipio} - ${candidato.estado}, CEP: ${candidato.cep}`
                : "Não informado"}
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h2 style={{ marginTop: 0 }}>Inscrições</h2>
        {loadingInscricoes ? (
          <p>Carregando inscrições...</p>
        ) : inscricoes.length === 0 ? (
          <p>Nenhuma inscrição encontrada.</p>
        ) : (
          <div className="stack-lg">
            {inscricoes.map((inscricao) => (
              <div
                key={inscricao.id}
                className="surface-section"
                style={{
                  borderLeft: inscricao.invalidada
                    ? "4px solid var(--cor-erro)"
                    : "4px solid var(--color-primary)",
                  padding: 14,
                  borderRadius: 12,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong>{inscricao.edital_titulo}</strong>
                    <div style={{ color: "rgba(15, 23, 42, 0.7)" }}>
                      Edital Nº {inscricao.numero_edital}/{inscricao.ano_edital} • Cargo: {inscricao.nome_cargo}
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <strong>Inscrição:</strong> {String(inscricao.id).padStart(4, "0")}
                      {inscricao.invalidada ? (
                        <span style={{ marginLeft: 10, color: "var(--cor-erro)", fontWeight: 800 }}>
                          INDEFERIDO
                        </span>
                      ) : null}
                    </div>
                    {inscricao.invalidada ? (
                      <div style={{ marginTop: 8, color: "rgba(127, 29, 29, 0.95)" }}>
                        <strong>Justificativa:</strong>{" "}
                        {inscricao.justificativa_invalidacao || "Não informada"}
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    {inscricao.invalidada ? (
                      <button
                        onClick={() => openRevalidar(inscricao)}
                        className="btn-modern btn-ghost"
                      >
                        Revalidar
                      </button>
                    ) : (
                      <button
                        onClick={() => openInvalidar(inscricao)}
                        className="btn-modern"
                        style={{ background: "var(--cor-erro)" }}
                      >
                        Invalidar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <JustificationModal
        isOpen={invalidacaoModalOpen}
        onClose={closeInvalidacaoModal}
        title={isRevalidarFlow ? "Revalidar inscrição" : "Invalidar inscrição"}
        message={
          isRevalidarFlow
            ? "Confirme para revalidar esta inscrição."
            : "Informe uma justificativa (o candidato verá essa mensagem)."
        }
        confirmLabel={isRevalidarFlow ? "Revalidar" : "Invalidar"}
        requireText={!isRevalidarFlow}
        placeholder={
          isRevalidarFlow
            ? "(sem justificativa)"
            : "Ex.: Documentos inconsistentes / CPF inválido / etc."
        }
        defaultValue={
          isRevalidarFlow
            ? ""
            : invalidacaoTarget?.justificativa_invalidacao || ""
        }
        onConfirm={submitInvalidacao}
      />
    </>
  );
}

export default DetalhesCandidato;
